// client/src/components/media/PostsList.jsx
// List of Timeline posts (event_items under the tenant's Timeline event).
// Each post shows date + caption/text + first media thumbnail.
// Clicking the thumbnail opens PostDetail for that post.

import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import AppFooter from "../AppFooter";
import { FaArrowLeft, FaPaperPlane, FaHome, FaBolt } from "react-icons/fa";
import { sessionRequestConfig } from "../../services/sessionLocal";
import SearchFilterBar from "../shared/SearchFilterBar";
import MediaViewToggle from "./MediaViewToggle";
import useMediaView from "../../hooks/useMediaView";
import LoadingState from "../common/LoadingState";
import ErrorState from "../common/ErrorState";
import EmptyState from "../common/EmptyState";

const toAbs = (baseUrl, p) =>
  !p ? p : /^https?:\/\//i.test(p) ? p : `${baseUrl}${p.startsWith("/") ? "" : "/"}${p}`;

const isVideoMedia = (m) =>
  (m?.mimetype?.startsWith?.("video/")) || /\.(mp4|mov|webm|ogg|avi|mkv)$/i.test(m?.path || "");

const ACTION_LABELS = {
  auction: "Auction",
  raffle: "Raffle",
  challenge: "Challenge",
  contest: "Contest",
  ama: "AMA",
  ugc_campaign: "UGC Campaign",
  themed_day: "Themed Day",
  collab_takeover: "Collab / Takeover",
  poll_quiz: "Poll / Quiz",
  live_qna: "Live Q&A",
  watch_party: "Watch Party",
  workshop: "Workshop",
};

const formatDate = (s) => {
  if (!s) return "";
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
};

const formatTime = (s) => {
  if (!s) return "";
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
};

const PostsList = () => {
  const { tenant } = useParams();
  const navigate = useNavigate();
  const baseUrl = process.env.REACT_APP_BASE_URL || (typeof window !== "undefined" ? window.location.origin : "http://localhost:5000");
  const cfg = sessionRequestConfig();

  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [view, setView] = useMediaView();
  // grid = 3 cols on every breakpoint (matches the Instagram-style mock);
  // list = single full-width column so media scales to the screen width on mobile.
  const colClass = view === "list" ? "col-12" : "col-4";

  useEffect(() => {
    if (!tenant) return;
    let alive = true;

    (async () => {
      try {
        const { data } = await axios.get(`/api/events/${tenant}/timeline/posts`, cfg);
        if (!alive) return;
        setPosts(Array.isArray(data?.posts) ? data.posts : []);
      } catch (err) {
        console.error("Error loading posts:", err);
        if (alive) setError(err?.response?.data?.message || err?.message || "Failed to load posts");
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => { alive = false; };
  }, [tenant]); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredPosts = useMemo(() => {
    let result = posts;
    const q = searchTerm.trim().toLowerCase();
    if (q) {
      result = result.filter((p) =>
        (p?.name || "").toLowerCase().includes(q) ||
        (p?.description || "").toLowerCase().includes(q)
      );
    }
    if (dateFrom) {
      const from = new Date(dateFrom);
      result = result.filter((p) => (p?.createdon ? new Date(p.createdon) >= from : true));
    }
    if (dateTo) {
      const to = new Date(dateTo + "T23:59:59");
      result = result.filter((p) => (p?.createdon ? new Date(p.createdon) <= to : true));
    }
    return result;
  }, [posts, searchTerm, dateFrom, dateTo]);

  const groupedPosts = useMemo(() => {
    const byDate = {};
    for (const p of filteredPosts) {
      const d = p?.createdon ? new Date(p.createdon) : null;
      const key = d && !Number.isNaN(d.getTime()) ? d.toISOString().slice(0, 10) : "unknown";
      (byDate[key] ||= []).push(p);
    }
    return Object.entries(byDate).sort(([a], [b]) => (a < b ? 1 : -1));
  }, [filteredPosts]);

  const handleNewPost = () => navigate(`/${tenant}/post/new`);
  const openPost = (postId) => navigate(`/${tenant}/post/${postId}`);

  const footerButtons = [
    { id: "back", icon: <FaArrowLeft />, label: "Back", onClick: () => navigate(-1), disabled: loading },
    { id: "home", icon: <FaHome />, label: "Home", onClick: () => navigate(`/${tenant}/dashboard`), disabled: loading },
    { id: "new", icon: <FaPaperPlane />, label: "New Post", onClick: handleNewPost, disabled: loading },
  ];

  // Previously a hardcoded, untranslated "Loading posts..." <div> and a raw
  // Bootstrap .alert-danger. Both now use the shared states, which are
  // translated and sit on an explicit surface.
  if (loading) {
    return (
      <div className="container">
        <h2 className="hdr hdr--primary hdr--lg">Posts</h2>
        <LoadingState messageKey="posts.loadingPosts" />
        <AppFooter buttons={footerButtons} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="container">
        <h2 className="hdr hdr--primary hdr--lg">Posts</h2>
        <ErrorState message={error} />
        <AppFooter buttons={footerButtons} />
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="flex items-center justify-between mb-3">
        <h3 className="m-0">Posts</h3>
        <div className="flex items-center" style={{ gap: 12 }}>
          <div className="text-gray-400">{posts.length} posts</div>
          <MediaViewToggle view={view} onChange={setView} />
        </div>
      </div>

      <SearchFilterBar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        dateFrom={dateFrom}
        dateTo={dateTo}
        onDateFromChange={setDateFrom}
        onDateToChange={setDateTo}
        onClear={() => { setSearchTerm(""); setDateFrom(""); setDateTo(""); }}
        searchPlaceholder="Search posts…"
      />

      <div className="desktop-action-bar">
        <button type="button" onClick={() => navigate(-1)}>
          <FaArrowLeft /> Back
        </button>
        <button type="button" onClick={() => navigate(`/${tenant}/dashboard`)}>
          <FaHome /> Home
        </button>
        <button type="button" className="btn-primary-action" onClick={handleNewPost}>
          <FaPaperPlane /> New Post
        </button>
      </div>

      {filteredPosts.length === 0 ? (
        <EmptyState messageKey="posts.emptyPrompt" />
      ) : (
        groupedPosts.map(([date, postArr]) => (
          <div key={date} className="mb-4">
            <h5 className="mb-3" style={{ color: "#6c757d" }}>
              {date === "unknown" ? "Undated" : formatDate(postArr[0]?.createdon)}
            </h5>
            <div className="row gap-3" style={{ alignItems: "stretch" }}>
              {postArr.map((post) => {
                const media = post.media;
                const thumbUrl = media ? toAbs(baseUrl, media.path) : null;
                const isVid = media ? isVideoMedia(media) : false;
                const actionLabel = post.action ? ACTION_LABELS[post.action] || post.action : null;

                return (
                  <div key={post.id} className={colClass}>
                    <div className="card h-full shadow-sm">
                      <button
                        type="button"
                        className="ratio ratio-4x3 bg-dark-bg flex items-center justify-center"
                        style={{
                          cursor: "pointer",
                          border: "none",
                          padding: 0,
                          width: "100%",
                          background: "#111",
                          position: "relative",
                        }}
                        onClick={() => openPost(post.id)}
                        aria-label={`Open post ${post.name || post.id}`}
                      >
                        {thumbUrl ? (
                          isVid ? (
                            <video
                              src={thumbUrl}
                              className="img-fluid"
                              style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
                              playsInline
                              muted
                              preload="metadata"
                            >
                              <track kind="captions" />
                            </video>
                          ) : (
                            <img
                              src={thumbUrl}
                              alt={post.name || "post"}
                              className="img-fluid"
                              loading="lazy"
                              style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", objectPosition: "center" }}
                            />
                          )
                        ) : (
                          <span style={{ color: "#666" }}>No media</span>
                        )}
                        {actionLabel && (
                          <span
                            style={{
                              position: "absolute",
                              top: 8,
                              left: 8,
                              background: "rgba(37,56,102,0.92)",
                              color: "#fff",
                              fontSize: 12,
                              padding: "2px 8px",
                              borderRadius: 999,
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 4,
                            }}
                          >
                            <FaBolt /> {actionLabel}
                          </span>
                        )}
                      </button>
                      <div className="card-body py-2">
                        <div className="small text-gray-400">{formatTime(post.createdon)}</div>
                        <div className="font-semibold truncate" title={post.name}>
                          {post.name || "Untitled post"}
                        </div>
                        {post.description && (
                          <div
                            className="small"
                            style={{
                              color: "#495057",
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                            }}
                          >
                            {post.description}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))
      )}

      <AppFooter buttons={footerButtons} />
    </div>
  );
};

export default PostsList;
